import { mathEditingPluginKey } from '@/components/tiptap-editor/tiptap-extension/math-extension/math-editing-extension';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { startCommentDraft } from '@/lib/comment-draft-store';
import { useEditorState, type Editor } from '@tiptap/react';
import { MessageSquarePlus } from 'lucide-react';
import { forwardRef } from 'react';

function isMathLatexBeingEdited(editor: Editor) {
    // Decomposed latex is editable text; comments should target the math node as a whole instead.
    return mathEditingPluginKey.getState(editor.state)?.pendingEdit != null;
}

/** Starts a comment on the selected text; the draft opens beside the text (or in the comment sidebar) to be written there. */
const CommentButton = forwardRef<HTMLButtonElement, { className?: string }>(function CommentButton({ className }, ref) {
    const { editor } = useTiptapEditor();
    const canComment =
        useEditorState({
            editor,
            selector: ({ editor: ed }) =>
                Boolean(ed && 'setThread' in ed.commands && !ed.state.selection.empty && !isMathLatexBeingEdited(ed)),
        }) ?? false;

    if (!editor || !('setThread' in editor.commands)) {
        return null;
    }

    return (
        <Button
            ref={ref}
            type="button"
            className={className}
            data-style="ghost"
            aria-label="Add comment"
            tooltip="Add comment"
            disabled={!canComment}
            onClick={() => {
                if (isMathLatexBeingEdited(editor)) {
                    return;
                }
                startCommentDraft(editor);
            }}
        >
            <MessageSquarePlus className="tiptap-button-icon" />
        </Button>
    );
});

export default CommentButton;
